class CustomAudioPlayerElement extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
        const src = this.getAttribute('src');
        const filename = this.getAttribute('filename') || 'Audio';

        this.shadowRoot.innerHTML = `
            <style>
                .player-container {
                    display: flex;
                    flex-direction: column;
                    background-color: #2a2a2a;
                    border-radius: 8px;
                    border: 1px solid rgba(255,255,255,0.1);
                    width: 100%;
                    max-width: 214px;
                    box-sizing: border-box;
                    margin-top: 10px;
                    margin-bottom: 0;
                    font-family: sans-serif;
                    overflow: hidden;
                    padding-top: 0;
                }
                .title {
                    font-size: 12px;
                    font-weight: 500;
                    color: #eee;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    padding: 0 12px;
                    margin-bottom: 4px;
                }
                .buttons-row {
                    display: flex;
                    align-items: center;
                    padding: 0 12px;
                    margin-bottom: 6px;
                }
                .btn {
                    width: 28px;
                    height: 28px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.1);
                    color: #bbb;
                    cursor: pointer;
                    border-radius: 6px;
                    box-sizing: border-box;
                }
                .btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
                
                .play-btn {
                    background: #10b981;
                    color: #fff;
                    border: none;
                    border-radius: 50%;
                }
                .play-btn:hover { background: #0ea5e9; }
                
                .time-container {
                    flex: 1;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 4px;
                }
                .time {
                    font-size: 10px;
                    color: #888;
                    font-variant-numeric: tabular-nums;
                }
                
                .speed-btn {
                    font-size: 10px;
                    font-weight: bold;
                    margin-right: 6px;
                }
                .download-btn {
                    text-decoration: none;
                }
                
                .range {
                    -webkit-appearance: none;
                    width: 100%;
                    height: 4px;
                    background: rgba(255,255,255,0.1);
                    outline: none;
                    margin: 0;
                    padding: 0;
                    cursor: pointer;
                    display: block;
                }
                .range::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 0;
                    height: 0;
                }
                .range::-moz-range-thumb {
                    width: 0;
                    height: 0;
                    border: none;
                }
                .range-wrapper {
                    position: relative;
                    height: 4px;
                    width: 100%;
                    background: rgba(255,255,255,0.1);
                }
                .range-progress {
                    position: absolute;
                    top: 0; left: 0; bottom: 0;
                    background: #10b981;
                    width: 0%;
                    pointer-events: none;
                }
                .range-input {
                    position: absolute;
                    top: 0; left: 0; width: 100%; height: 100%;
                    opacity: 0;
                    cursor: pointer;
                    margin: 0;
                }
            </style>
            <div class="player-container">
                <audio preload="metadata"></audio>
                <div class="title" title="${filename}">${filename}</div>
                <div class="buttons-row">
                    <button class="btn play-btn">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                    </button>
                    
                    <div class="time-container">
                        <span class="time current-time">0:00</span>
                        <span class="time" style="color: #555;">/</span>
                        <span class="time duration">0:00</span>
                    </div>

                    <button class="btn speed-btn">1x</button>
                    <a href="${src}" target="_blank" download="${filename}" class="btn download-btn" title="Descargar">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                    </a>
                </div>
                <div class="range-wrapper">
                    <div class="range-progress"></div>
                    <input type="range" class="range-input" min="0" max="0" value="0">
                </div>
            </div>
        `;

        this.audio = this.shadowRoot.querySelector('audio');
        this.playBtn = this.shadowRoot.querySelector('.play-btn');
        this.range = this.shadowRoot.querySelector('.range-input');
        this.progress = this.shadowRoot.querySelector('.range-progress');
        this.currentTimeEl = this.shadowRoot.querySelector('.current-time');
        this.durationEl = this.shadowRoot.querySelector('.duration');
        this.speedBtn = this.shadowRoot.querySelector('.speed-btn');

        this.isPlaying = false;
        this.playbackRate = 1;

        if (src) {
            this.audio.src = src;
            this.audio.load();
        }

        this.attachEventListeners();
    }

    attachEventListeners() {
        this.audio.addEventListener('timeupdate', () => {
            this.range.value = this.audio.currentTime;
            this.currentTimeEl.textContent = this.formatTime(this.audio.currentTime);
            const pct = this.audio.duration ? (this.audio.currentTime / this.audio.duration) * 100 : 0;
            this.progress.style.width = pct + '%';
        });

        this.audio.addEventListener('loadedmetadata', () => {
            this.range.max = this.audio.duration;
            this.durationEl.textContent = this.formatTime(this.audio.duration);
        });

        this.audio.addEventListener('ended', () => {
            this.isPlaying = false;
            this.updatePlayIcon();
        });

        this.playBtn.addEventListener('click', () => {
            if (this.isPlaying) {
                this.audio.pause();
                this.isPlaying = false;
                this.updatePlayIcon();
            } else {
                const playPromise = this.audio.play();
                if (playPromise !== undefined) {
                    playPromise.then(() => {
                        this.isPlaying = true;
                        this.updatePlayIcon();
                    }).catch(error => {
                        console.error("Audio playback error:", error);
                        this.isPlaying = false;
                        this.updatePlayIcon();
                    });
                } else {
                    this.isPlaying = true;
                    this.updatePlayIcon();
                }
            }
        });

        this.range.addEventListener('input', (e) => {
            const time = Number(e.target.value);
            this.audio.currentTime = time;
            this.currentTimeEl.textContent = this.formatTime(time);
            const pct = this.audio.duration ? (time / this.audio.duration) * 100 : 0;
            this.progress.style.width = pct + '%';
        });

        this.speedBtn.addEventListener('click', () => {
            const rates = [1, 1.5, 2];
            this.playbackRate = rates[(rates.indexOf(this.playbackRate) + 1) % rates.length];
            this.audio.playbackRate = this.playbackRate;
            this.speedBtn.textContent = this.playbackRate + 'x';
        });
    }

    updatePlayIcon() {
        if (this.isPlaying) {
            this.playBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>';
        } else {
            this.playBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>';
        }
    }

    formatTime(time) {
        if (!time || isNaN(time)) return '0:00';
        const m = Math.floor(time / 60);
        const s = Math.floor(time % 60);
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    }
}

if (!customElements.get('custom-audio-player')) {
    customElements.define('custom-audio-player', CustomAudioPlayerElement);
}
